import { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  App as AntApp,
} from 'antd';
import { PlusOutlined, SendOutlined, UndoOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';

import {
  useCreateCustomerMutation,
  useCustomersQuery,
  useRestoreCustomerMutation,
  type Customer,
  type CustomersQueryParams,
} from './hooks';
import { ErrorAlert } from '@/components/ErrorAlert';
import { BlockedActionTooltip } from '@/components/BlockedActionTooltip';
import { useTenantBlocked } from '@/settings/hooks';
import { ApiError } from '@starling/core/api/errors';
import { formatDateTime } from '@/lib/format';

const { Title, Text } = Typography;

// Значение Segmented-фильтра: три обычных режима по привязке + корзина.
type SegmentValue = 'all' | 'true' | 'false' | 'deleted';

interface CreateValues {
  name: string;
  phone: string;
  consent_marketing?: boolean;
}

function parseLinkedParam(raw: string | null): CustomersQueryParams['linked'] {
  if (raw === 'true' || raw === 'false') return raw;
  return 'all';
}

export function CustomersPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<CreateValues>();

  const page = Number(searchParams.get('page') ?? '1') || 1;
  const pageSize = Number(searchParams.get('page_size') ?? '20') || 20;
  const deleted = searchParams.get('deleted') === 'true';
  const linked = parseLinkedParam(searchParams.get('linked'));
  const segment: SegmentValue = deleted ? 'deleted' : (linked ?? 'all');

  const queryParams: CustomersQueryParams = deleted
    ? { page, pageSize, deleted: true }
    : { page, pageSize, linked };
  const { data, isLoading, error } = useCustomersQuery(queryParams);
  const createMutation = useCreateCustomerMutation();
  const restoreMutation = useRestoreCustomerMutation();
  const { isBlocked } = useTenantBlocked();

  const setSegment = (next: SegmentValue): void => {
    const params = new URLSearchParams(searchParams);
    params.delete('linked');
    params.delete('deleted');
    if (next === 'deleted') params.set('deleted', 'true');
    else if (next === 'true' || next === 'false') params.set('linked', next);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleRestore = (record: Customer): void => {
    restoreMutation.mutate(record.id, {
      onSuccess: () => {
        void message.success(t('customers.restored'));
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'customer_phone_already_exists') {
          void message.error(t('customers.errors.phone_already_exists'));
        } else {
          void message.error(err instanceof Error ? err.message : t('errors.generic'));
        }
      },
    });
  };

  const onCreateSubmit = (values: CreateValues): void => {
    createMutation.mutate(
      {
        name: values.name,
        phone: values.phone,
        consent_marketing: values.consent_marketing ?? false,
      },
      {
        onSuccess: (created) => {
          form.resetFields();
          setCreateOpen(false);
          void message.success(t('common.create'));
          navigate(`/customers/${created.id}`);
        },
        onError: (err) => {
          void message.error(err instanceof Error ? err.message : t('errors.generic'));
        },
      },
    );
  };

  const baseColumns: ColumnsType<Customer> = [
    {
      title: t('customers.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: t('customers.table.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 180,
      render: (phone: string) => <span className="sl-mono">{phone}</span>,
    },
    {
      title: t('customers.table.telegram'),
      key: 'telegram',
      width: 200,
      render: (_v, c) =>
        c.linked_at ? (
          <Tag color="cyan" icon={<SendOutlined />}>
            {t('customers.linked_badge')}
          </Tag>
        ) : (
          <Tag>{t('customers.unlinked_badge')}</Tag>
        ),
    },
    {
      title: t('customers.table.marketing'),
      key: 'marketing',
      width: 110,
      align: 'center',
      render: (_v, c) =>
        c.consent_marketing ? (
          <Tag color="green">{t('common.yes')}</Tag>
        ) : (
          <Tag>{t('common.no')}</Tag>
        ),
    },
  ];

  const createdColumn: ColumnsType<Customer>[number] = {
    title: t('customers.table.created'),
    dataIndex: 'created_at',
    key: 'created_at',
    width: 160,
    render: (val: string) => (
      <Text type="secondary" className="sl-mono">
        {new Date(val).toLocaleDateString('ru-RU')}
      </Text>
    ),
  };

  const trashColumns: ColumnsType<Customer> = [
    {
      title: t('customers.deleted_at'),
      dataIndex: 'deleted_at',
      key: 'deleted_at',
      width: 170,
      render: (val: string | null) => (
        <Text type="secondary" className="sl-mono">
          {formatDateTime(val)}
        </Text>
      ),
    },
    {
      title: t('customers.table.actions'),
      key: 'actions',
      width: 170,
      render: (_v, record) => (
        <Button
          icon={<UndoOutlined />}
          loading={restoreMutation.isPending && restoreMutation.variables === record.id}
          onClick={() => handleRestore(record)}
        >
          {t('customers.restore')}
        </Button>
      ),
    },
  ];

  const columns: ColumnsType<Customer> = deleted
    ? [...baseColumns, ...trashColumns]
    : [...baseColumns, createdColumn];

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const linkedCount = items.filter((c) => c.linked_at !== null).length;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('customers.title')}
          </Title>
          <Text type="secondary">{t('customers.subtitle')}</Text>
        </div>
        {!deleted && (
          <BlockedActionTooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              disabled={isBlocked}
              onClick={() => setCreateOpen(true)}
            >
              {t('customers.new_button')}
            </Button>
          </BlockedActionTooltip>
        )}
      </div>

      <Card style={{ marginBottom: 16 }} variant="outlined">
        <Segmented<SegmentValue>
          value={segment}
          onChange={(v) => setSegment(v as SegmentValue)}
          options={[
            { label: t('customers.filters.all'), value: 'all' },
            { label: t('customers.filters.linked'), value: 'true' },
            { label: t('customers.filters.unlinked'), value: 'false' },
            { label: t('customers.filters.deleted'), value: 'deleted' },
          ]}
        />
      </Card>

      {error && <ErrorAlert error={error} />}

      <Table<Customer>
        rowKey="id"
        loading={isLoading}
        dataSource={items}
        columns={columns}
        onRow={(record) =>
          deleted
            ? {}
            : {
                onClick: () => navigate(`/customers/${record.id}`),
                style: { cursor: 'pointer' },
              }
        }
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p, ps) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(p));
            params.set('page_size', String(ps));
            setSearchParams(params);
          },
        }}
        locale={{
          emptyText: (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {t(deleted ? 'customers.empty_trash.title' : 'customers.empty.title')}
              </Text>
              <Text type="secondary">
                {t(deleted ? 'customers.empty_trash.subtitle' : 'customers.empty.subtitle')}
              </Text>
            </div>
          ),
        }}
      />

      {!deleted && (
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          {t('customers.footer', {
            shown: items.length,
            total,
            linked: linkedCount,
            percent: total > 0 ? Math.round((linkedCount / total) * 100) : 0,
          })}
        </Text>
      )}

      <Modal
        title={t('customers.create_modal.title')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
        okText={t('customers.create_modal.submit')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form<CreateValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onCreateSubmit}
          initialValues={{ consent_marketing: false }}
        >
          <Form.Item
            label={t('customers.create_modal.name')}
            name="name"
            rules={[{ required: true, min: 1, max: 200 }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            label={t('customers.create_modal.phone')}
            name="phone"
            rules={[{ required: true, min: 1 }]}
          >
            <Input placeholder="+7 701 123 45 67" />
          </Form.Item>
          <Form.Item
            label={t('customers.create_modal.consent_marketing')}
            name="consent_marketing"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
        {createMutation.error && (
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
            <ErrorAlert error={createMutation.error} />
          </Space>
        )}
      </Modal>
    </div>
  );
}
